import { ReactNode } from 'react';
import { formatAmount, InterestAccount, SupportedCurrencies, Tooltip, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { DetailRowsCard } from 'UI';
import styles from './InterestAccountCard.module.scss';

const cn = withStyles(styles);

type InterestAccountCardProps = {
  title: ReactNode;
  currency: SupportedCurrencies;
  account: InterestAccount;
  className?: string;
};

export default function InterestAccountCard({ title, currency, account, className }: InterestAccountCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.InterestAccountsDrawer.InterestAccountCard' });

  return (
    <DetailRowsCard
      className={className}
      title={title}
      details={[
        {
          label: t('availableBalance', { defaultValue: 'Available Balance' }),
          value: (
            <>
              {currency} {formatAmount(account.CurrentBalance.toString())}
            </>
          ),
        },
        {
          label: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <div>{t('eligibleBalance', { defaultValue: 'End of Day Eligible Balance' })}</div>

              <div>
                <Tooltip
                  borderColor="rgba(45, 45, 45, 0.6)"
                  icon="info"
                  position="bottom"
                  bgColor="#fbfbfb"
                  information={
                    account.InterestMaturityType === 'short-term'
                      ? t('ElibigleBalance.Tooltip.standard', {
                          defaultValue:
                            'Interest accrues on settled funds held in your VoPay account for at least 24 hours, including the reserve amount. Your Eligible Balance reflects these funds as of 11:59:59 pm EST',
                        })
                      : t('ElibigleBalance.Tooltip.premium', {
                          defaultValue:
                            'Interest accrues on settled funds that have remained in your VoPay account for a minimum of 5 consecutive days, including the reserve amount. Your Eligible Balance reflects these funds as of 11:59:59 pm EST.',
                        })
                  }
                  popoverClassName={cn('tooltip')}
                />
              </div>
            </div>
          ),
          value: (
            <>
              {currency} {formatAmount(account.EligibleBalance.toString())}
            </>
          ),
        },
        {
          label: t('interestEarned', { defaultValue: 'Interest Earned' }),
          value: (
            <>
              {currency} {formatAmount(account.InterestAccrued.toString())}
            </>
          ),
        },
      ]}
    />
  );
}
